import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/axios';

/**
 * Hook para verificar y gestionar permisos de usuario
 * Usa el sistema de permisos v2 basado en grupos
 */
export const usePermisos = () => {
  const { user } = useAuth();
  const [permisosCache, setPermisosCache] = useState({});
  const [loading, setLoading] = useState(false);

  // Roles con acceso total
  const ADMIN_ROLES = ['admin', 'superadmin', 'administrador'];

  /**
   * Verifica si el usuario es administrador
   */
  const isAdmin = useCallback(() => {
    if (!user?.rol) return false;
    return ADMIN_ROLES.includes(user.rol.toLowerCase());
  }, [user]);

  /**
   * Carga los permisos para un módulo específico usando permisos-v2
   */
  const loadPermisosModulo = useCallback(async (moduloId) => {
    if (!user || !moduloId) return {};
    
    // Si es admin, tiene todos los permisos
    if (isAdmin()) {
      return { __all__: true };
    }

    // Revisar cache
    if (permisosCache[moduloId]) {
      return permisosCache[moduloId];
    }

    try {
      setLoading(true);
      
      // Usar el endpoint de permisos-v2 que considera grupos
      const response = await api.get('/permisos-v2/mis-permisos', {
        params: { modulo_id: moduloId }
      });

      const permisosPorSeccion = {};
      
      // Procesar la respuesta del nuevo formato
      if (response.data?.modulos?.length > 0) {
        const modulo = response.data.modulos[0];
        
        // Verificar si hay permisos a nivel de módulo (sistema viejo)
        const permisosModuloBase = modulo.permisos_modulo?.acciones || {};
        const tienePermisoVerModulo = permisosModuloBase.ver === true;
        
        modulo.secciones?.forEach(s => {
          const accionesSeccion = s.acciones || {};
          
          // Si tiene permiso de ver a nivel módulo y no hay config específica de sección,
          // heredar el permiso de ver del módulo
          const tieneVerSeccion = accionesSeccion.ver === true;
          const verFinal = tieneVerSeccion || (tienePermisoVerModulo && accionesSeccion.ver === undefined);
          
          permisosPorSeccion[s.seccion_id] = {
            ver: verFinal,
            crear: accionesSeccion.crear === true || (permisosModuloBase.crear === true && accionesSeccion.crear === undefined),
            editar: accionesSeccion.editar === true || (permisosModuloBase.editar === true && accionesSeccion.editar === undefined),
            eliminar: accionesSeccion.eliminar === true || (permisosModuloBase.eliminar === true && accionesSeccion.eliminar === undefined),
            aprobar: accionesSeccion.aprobar === true || (permisosModuloBase.aprobar === true && accionesSeccion.aprobar === undefined),
            exportar: accionesSeccion.exportar === true || (permisosModuloBase.exportar === true && accionesSeccion.exportar === undefined),
            ejecutar: accionesSeccion.ejecutar === true || (permisosModuloBase.ejecutar === true && accionesSeccion.ejecutar === undefined),
            reasignar: accionesSeccion.reasignar === true,
            comentar: accionesSeccion.comentar === true,
            alcance: s.alcance
          };
        });
      }

      // Actualizar cache
      setPermisosCache(prev => ({
        ...prev,
        [moduloId]: permisosPorSeccion
      }));

      return permisosPorSeccion;
    } catch (error) {
      console.error('Error cargando permisos:', error);
      return {};
    } finally {
      setLoading(false);
    }
  }, [user, isAdmin, permisosCache]);

  /**
   * Verifica si el usuario tiene un permiso específico
   */
  const checkPermiso = useCallback(async (moduloId, seccionId, permiso = 'ver') => {
    // Admins tienen todos los permisos
    if (isAdmin()) return true;

    const permisosModulo = await loadPermisosModulo(moduloId);
    
    // Si es el permiso especial __all__, es admin
    if (permisosModulo.__all__) return true;
    
    // Si no hay permisos configurados para este módulo, NO permitir
    if (Object.keys(permisosModulo).length === 0) {
      return false;
    }

    const permisosSeccion = permisosModulo[seccionId];
    
    // Si no hay permisos específicos para esta sección, NO permitir ver
    if (!permisosSeccion) {
      return false;
    }

    return permisosSeccion[permiso] === true;
  }, [isAdmin, loadPermisosModulo]);

  /**
   * Filtra secciones basándose en permisos
   */
  const filterSeccionesConPermiso = useCallback((secciones, permisosModulo) => {
    // Admins ven todo
    if (isAdmin()) return secciones;
    
    // Si es el permiso especial __all__, es admin
    if (permisosModulo?.__all__) return secciones;

    // Si no hay permisos configurados, NO mostrar nada (más seguro)
    if (!permisosModulo || Object.keys(permisosModulo).length === 0) {
      return [];
    }

    return secciones.filter(seccion => {
      const permisos = permisosModulo[seccion.id];
      
      // Si no hay permiso específico para esta sección, NO mostrar
      if (!permisos) return false;
      
      // Solo mostrar si tiene permiso de ver explícitamente
      return permisos.ver === true;
    });
  }, [isAdmin]);

  /**
   * Invalida el cache de permisos (útil después de modificar permisos)
   */
  const invalidateCache = useCallback(() => {
    setPermisosCache({});
  }, []);

  return {
    checkPermiso,
    loadPermisosModulo,
    filterSeccionesConPermiso,
    invalidateCache,
    isAdmin,
    loading
  };
};

export default usePermisos;
